import { ProductBuilderOptions, ProductBuilderSelection } from "../types/ProductBuilderTypes";
import {
  MerchandisingProductView,
  MerchandisingDetail,
  Quantities,
  MpvRange,
} from "../types/ContentfulTypes";
import { isMailingServicesMpv, mailingServiceQuantities } from "./mailingServicesUtil";
import { replaceCloudinaryUrlTokens } from "./cloudinaryUrlUtil";
import { isInRanges } from "./isInRanges";

export class MpvProvider {
  private mpv?: MerchandisingProductView;

  constructor(mpv: MerchandisingProductView) {
    this.mpv = mpv;
  }

  getMpv() {
    return this.mpv as MerchandisingProductView;
  }

  getAllOptionsAndValues(): Record<string, string[]> {
    const allOptionsAndValues: Record<string, string[]> = {};
    this.mpv?.merchandisingDetails?.forEach(merchandisingDetail => {
      for (const property of merchandisingDetail.properties) {
        if (property.options) {
          let propObj = allOptionsAndValues[property.key];
          if (!propObj) {
            propObj = [];
            allOptionsAndValues[property.key] = propObj;
          }
          const valuesSet = new Set<string>(propObj);
          property.options.forEach(value => valuesSet.add(value.key));
          allOptionsAndValues[property.key] = Array.from(valuesSet);
        }
      }
    });
    return allOptionsAndValues;
  }

  getAllRanges(): Record<string, MpvRange[]> {
    const allRanges: Record<string, MpvRange[]> = {};
    this.mpv?.merchandisingDetails?.forEach(merchandisingDetail => {
      for (const property of merchandisingDetail.properties) {
        if (property.ranges) {
          let propObj = allRanges[property.key];
          if (!propObj) {
            propObj = [];
            allRanges[property.key] = propObj;
          }
          const valuesSet = new Set<MpvRange>(propObj);
          property.ranges.forEach(range => valuesSet.add(range));
          allRanges[property.key] = Array.from(valuesSet);
        }
      }
    });
    return allRanges;
  }

  getPreselectedOptions(productBuilderOptions?: ProductBuilderOptions) {
    const preselectedOptions = Object.assign({}, this.findPreselectedOptions());

    if (!productBuilderOptions) {
      return preselectedOptions;
    }

    // Validate preselected options with builder data.
    // If a pre-selected option is not part of product builder, ignore it.
    const validPreselectedOptions: Record<string, string> = {};
    Object.entries({ ...preselectedOptions }).forEach(
      ([preselectedOptionKey, preselectedOptionValue]: [string, string]) => {
        if (productBuilderOptions?.selections) {
          productBuilderOptions.selections.forEach(builderSelection => {
            if (
              this.validatePreselectedOption(
                builderSelection,
                preselectedOptionKey,
                preselectedOptionValue
              )
            ) {
              validPreselectedOptions[preselectedOptionKey] = preselectedOptionValue;
            }
          });
        }
      }
    );
    return validPreselectedOptions;
  }

  getOption(optionKey: string) {
    const merchDetails = this.getProductPageMerchandisingDetails();
    const option = merchDetails?.properties?.find(option => option.key === optionKey);
    return option;
  }

  getOptionKeys(): string[] {
    const merchDetails = this.getProductPageMerchandisingDetails();
    return merchDetails?.properties.map(property => property.key) ?? [];
  }

  getValuesOfOption(optionKey: string) {
    const option = this.getOption(optionKey);
    return option?.options ?? [];
  }

  getProductPageMerchandisingDetails() {
    return this.mpv?.merchandisingDetails?.find(
      (merchandisingDetail: MerchandisingDetail) => merchandisingDetail.type === "Product Page"
    );
  }

  getUploadFlowMerchandisingDetails() {
    return this.mpv?.merchandisingDetails?.find(
      (merchandisingDetail: MerchandisingDetail) => merchandisingDetail.type === "UploadFlow"
    );
  }

  getDefaultProductImageUrl() {
    const rawCloudinaryUrl = this.mpv?.defaultProductImage?.cloudinaryImage?.cloudinaryUrl;
    const cloudinaryUrl = rawCloudinaryUrl
      ? replaceCloudinaryUrlTokens(rawCloudinaryUrl, { IMAGE_WIDTH: "150" })
      : undefined;
    return cloudinaryUrl;
  }

  getQuantities(locale: string): Quantities | undefined {
    return isMailingServicesMpv(this.mpv?.mpvId ?? "", locale)
      ? mailingServiceQuantities
      : this.mpv?.quantities;
  }

  private findPreselectedOptions(): Record<string, string> {
    return (
      this.mpv?.defaultPreselectedOptions?.reduce((result, preselectedOptions) => {
        result = {
          ...result,
          ...preselectedOptions.coreModelMappings,
        };
        return result;
      }, {}) || {}
    );
  }

  private validatePreselectedOption(
    builderOption: ProductBuilderSelection,
    preselectedOptionKey: string,
    preselectedOptionValue: string
  ): boolean {
    if (builderOption?.property?.key === preselectedOptionKey) {
      if (builderOption?.property?.options?.find(v => v.key === preselectedOptionValue)) {
        return true;
      }
      if (isInRanges(preselectedOptionValue, builderOption?.property?.ranges || [])) {
        return true;
      }
    }

    return false;
  }
}
